/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SWMForm.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Button, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import CustomFormTextFieldV2 from "../../common/textField/CustomFormTextFieldV2";
import MultiselectConnectorV2 from "../../common/connectors/MultiselectConnectorV2";
import appTheme from "../../../../assets/AppTheme/appTheme";
import { useDispatch } from "react-redux";
import { HandleApiActions, labCar } from "../../../../redux/actions";
import typeApprovalAPI from "../../../../redux/actions/typeApprovalAPI";
import ruleEngineActions from "../../../../redux/actions/ruleEngineAction";
import CustomSelectV2 from "../../common/select/CustomSelectV2";

function SWMForm({
  openSWModuleReplaceDialog,
  setOpenSWModuleReplaceDialog,
  handleSWModuleReplace,
  selectedSWModuleReplaceDetails,
  setSelectedSWModuleReplaceDetails,
  vfOptionsList,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  title,
  supplierOptionsList,
  disableFields = [],
}) {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [version, setVersion] = useState("");
  const [supplierName, setSupplierName] = useState([]);
  const [versionError, setVersionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [selectedVfIds, setSelectedVfIds] = useState([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [discp, setDiscp] = useState("");
  const [githubLink, setGithubLink] = useState("");

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);

  const dispatch = useDispatch();

  const normalizeArrayValue = (value) => {
    if (Array.isArray(value)) {
      return value.filter(
        (item) => item !== null && item !== undefined && item !== "",
      );
    }

    return value !== null && value !== undefined && value !== "" ? [value] : [];
  };

  const normalizeKeywordIds = (keywords) =>
    (Array.isArray(keywords) ? keywords : [])
      .map((keyword) =>
        keyword && typeof keyword === "object"
          ? keyword?.id ?? keyword?.value
          : keyword,
      )
      .filter((id) => id !== null && id !== undefined && id !== "");

  const selectedSupplierOptions = normalizeArrayValue(
    selectedSWModuleReplaceDetails?.supplier_name,
  ).map((supplier) => ({ value: supplier, label: supplier }));

  const supplierOptionsForView = [
    ...(supplierOptionsList || []),
    ...selectedSupplierOptions.filter(
      (selectedSupplier) =>
        !(supplierOptionsList || []).some(
          (option) => String(option?.value) === String(selectedSupplier.value),
        ),
    ),
  ];

  const keywordOptionsForView = (
    selectedSWModuleReplaceDetails?.keywords || []
  ).map((keyword) => ({
    value: keyword?.id ?? keyword?.value ?? keyword,
    label: keyword?.name ?? keyword?.label ?? keyword,
  }));

  useEffect(() => {
    if (Boolean(openSWModuleReplaceDialog) && selectedSWModuleReplaceDetails) {
      setName(selectedSWModuleReplaceDetails?.name || "");
      setVersion(selectedSWModuleReplaceDetails?.version || "");
      setDescription(selectedSWModuleReplaceDetails?.description || "");
      setSupplierName(
        normalizeArrayValue(selectedSWModuleReplaceDetails?.supplier_name),
      );
      setSelectedKeywordIds(
        normalizeKeywordIds(selectedSWModuleReplaceDetails?.keywords),
      );
      setSummary(selectedSWModuleReplaceDetails?.summary || "");
      setSelectedVfIds(
        (selectedSWModuleReplaceDetails?.vf_ids || [])
          ?.map(Number)
          ?.filter((vfId) => !Number.isNaN(vfId)),
      );
      setDiscp(selectedSWModuleReplaceDetails?.reason || "");
      setGithubLink(
        selectedSWModuleReplaceDetails?.meta_details?.linked_meta_link || "",
      );
    }
  }, [
    openSWModuleReplaceDialog,
    selectedSWModuleReplaceDetails,
    selectedSingleVariantDetails?.variant_id,
    variantOptionList,
  ]);

  const clearReplaceSWModuleFields = () => {
    setName("");
    setNameError("");
    setVersion("");
    setVersionError("");
    setDescription("");
    setDescriptionError("");
    setSummary("");
    setSelectedVfIds([]);
    setSelectedKeywordIds([]);
    setDiscp("");
  };

  const handleCloseSWModuleReplace = () => {
    setOpenSWModuleReplaceDialog(false);
    clearReplaceSWModuleFields();
    setSelectedSWModuleReplaceDetails(null);
    setShowConfirmation(false);
    setShowIntersectionImpactPrompt(false);
  };

  const confirmReplaceAndRedirect = async ({
    selectedSWModuleReplaceDetailsParam,
    willImptIntrOnly = false,
  }) => {
    const targetDetails =
      selectedSWModuleReplaceDetailsParam || selectedSWModuleReplaceDetails;
    if (targetDetails?.swm_id) {
      const reqObj = {
        ...(supplierName && { supplier_name: supplierName }),
        current_time: new Date().toISOString(),
        project_id: Number(targetDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(targetDetails?.ee_id),
        swm_id: Number(targetDetails?.swm_id),
        variants_ids: selectedEEComponentDetails?.variant_ids,
        version: version,
        name: name,
        ...(summary && { summary }),
        description: description,
        vf_ids: selectedVfIds,
        will_it_impact: true,
        reason: discp,
      };
      try {
        const response = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.replace_sw_module,
              params: reqObj,
              show_toast: true,
            }),
          ),
        );
        if (!response.error) {
          handleCloseSWModuleReplace();
          if (willImptIntrOnly) {
            await Promise.resolve(
              dispatch(
                HandleApiActions({
                  ...labCar.update_action_type,
                  params: {
                    current_time: new Date().toISOString(),
                    project_id: Number(targetDetails?.project_id),
                    variant_id: Number(
                      selectedSingleVariantDetails?.variant_id,
                    ),
                    ee_id: Number(targetDetails?.ee_id),
                    swm_id: Number(targetDetails?.swm_id),
                    is_dependency_linking: true,
                  },
                  show_toast: false,
                }),
              ),
            );
          }
          getSWModulesList();
        }
      } catch (error) {
        console.error("Error in confirmReplaceAndRedirect:", error);
      }
    }
  };

  const handleCloseSWModuleReplaceDialog = () => {
    setOpenSWModuleReplaceDialog(false);
  };

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      tickets_type: 1,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...typeApprovalAPI.get_tickets,
            params: reqObj,
            show_toast: false,
          }),
        ),
      );

      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
              .map((ticket) => ticket?.ls_details?.standard_number)
              .filter(Boolean)
            : [],
        ),
      ];

      if (standardsWithRuleSet.length > 0) {
        const standardWithRuleSet = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...ruleEngineActions.get_rule_engine_with_standards,
              params: { standard: standardsWithRuleSet },
              show_toast: false,
            }),
          ),
        );

        if (
          standardWithRuleSet?.data &&
          (Array.isArray(standardWithRuleSet.data)
            ? standardWithRuleSet.data.length > 0
            : Object.keys(standardWithRuleSet.data).length > 0)
        ) {
          handleCloseSWModuleReplaceDialog();
        } else {
          updateChangesWithoutStandardAndClose();
        }
        return standardWithRuleSet.data || [];
      }
      replaceChangesAndClose();
      return [];
    } catch (error) {
      console.error("Error checking standards with rule set:", error);
      return false;
    }
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      current_time: new Date().toISOString(),
      variant_ids: selectedEEComponentDetails?.variant_ids,
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      version: version,
      will_it_impact: false,
      requires_homologation: true,
      reason: discp,
    };
    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.update_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );
      getSWModulesList();
      if (!response.error) {
        handleCloseSWModuleReplace();
      }
    } catch (error) {
      console.error("Error updating changes without standard:", error);
    }
  };

  const replaceChangesAndClose = async () => {
    if (selectedSWModuleReplaceDetails?.swm_id) {
      const reqObj = {
        supplier_name: supplierName,
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
        swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        variant_ids: selectedEEComponentDetails?.variant_ids,
        name: name,
        ...(summary && { summary }),
        description: description,
        vf_ids: selectedVfIds,
        version: version,
        will_it_impact: false,
        requires_homologation: false,
        reason: discp,
      };
      try {
        const response = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.replace_sw_module,
              params: reqObj,
              show_toast: true,
            }),
          ),
        );
        getSWModulesList();
        if (!response.error) {
          handleCloseSWModuleReplace();
        }
      } catch (error) {
        console.error("Error replacing changes:", error);
      }
    }
  };

  return (
    <Stack spacing={2} mb={2}>
      <CustomFormTextFieldV2
        size={"small"}
        label={"Number"}
        placeholder={"Number"}
        type={"text"}
        fldType={"text"}
        value={name}
        setValue={setName}
        valueError={nameError}
        setValueError={setNameError}
        disabled={disableFields.includes("number")}
      />
      <CustomFormTextFieldV2
        size={"small"}
        label={"Version"}
        placeholder={"Version"}
        type={"text"}
        fldType={"textR"}
        value={version}
        setValue={setVersion}
        valueError={versionError}
        setValueError={setVersionError}
        disabled={disableFields.includes("version")}
      />
      <CustomFormTextFieldV2
        size={"small"}
        label={"Description"}
        placeholder={"Description"}
        type={"text"}
        fldType={"text"}
        value={description}
        setValue={setDescription}
        valueError={descriptionError}
        setValueError={setDescriptionError}
        disabled={disableFields.includes("description")}
      />
      <CustomFormTextFieldV2
        size={"small"}
        label={"Summary"}
        placeholder={"Summary"}
        type={"text"}
        value={summary}
        setValue={setSummary}
        disabled={disableFields.includes("summary")}
      />

      {disableFields.includes("supplierName") ? (
        <MultiselectConnectorV2
          multiple
          selectedValues={supplierName || []}
          setSelectedValues={() => {}}
          label={"Select Supplier Name"}
          optionsList={supplierOptionsForView}
          placeholder={false}
          disabled
        />
      ) : (
        <CustomSelectV2
          required={false}
          size={"small"}
          label={"Select Supplier Name"}
          optionsList={supplierOptionsList}
          selectedValue={supplierName?.[0] || ""}
          handleOnChange={(e) =>
            setSupplierName(normalizeArrayValue(e.target.value))
          }
          disabled={disableFields.includes("supplierName")}
        />
      )}
      <MultiselectConnectorV2
        multiple
        selectedValues={selectedKeywordIds || []}
        setSelectedValues={setSelectedKeywordIds}
        disabled={disableFields.includes("keywords")}
        label={"Keywords"}
        optionsList={keywordOptionsForView}
        placeholder={false}
      />
      <CustomFormTextFieldV2
        size="small"
        label="GitHub Link"
        placeholder="https://github.com/username/repo"
        type="text"
        fldType="text"
        value={githubLink}
        setValue={(val) => {
          setGithubLink(val);
        }}
        disabled={true}
      />

      <MultiselectConnectorV2
        size={"small"}
        label={"Select Vehicle Functions"}
        optionsList={
          selectedSingleVariantDetails?.vehicle_function_details?.map((el) => {
            return {
              value: el.id,
              label: `${el.abbreviation} - ${el.name}`,
            };
          }) || []
        }
        selectedValues={selectedVfIds}
        setSelectedValues={setSelectedVfIds}
        disabled={true}
      />

      {showConfirmation && (
        <Stack spacing={2}>
          <Typography variant="body1">
            Will it impact the existing dependencies?
          </Typography>
          <Stack direction="row" spacing={2} justifyContent={"center"}>
            <Button
              sx={{ ...appTheme.commonBtnStyle }}
              onClick={() =>
                confirmReplaceAndRedirect({
                  selectedSWModuleReplaceDetailsParam:
                    selectedSWModuleReplaceDetails,
                })
              }
            >
              Yes
            </Button>
            <Button
              sx={{ ...appTheme.commonBtnStyle }}
              onClick={() => {
                setShowConfirmation(false);
                setShowIntersectionImpactPrompt(true);
              }}
            >
              No
            </Button>
          </Stack>
        </Stack>
      )}

      {showIntersectionImpactPrompt && (
        <Stack spacing={2}>
          <Typography variant="body1">
            Will it impact the existing intersection?
          </Typography>
          <Stack direction="row" spacing={2} justifyContent={"center"}>
            <Button
              sx={{ ...appTheme.commonBtnStyle }}
              onClick={() =>
                confirmReplaceAndRedirect({
                  selectedSWModuleReplaceDetailsParam:
                    selectedSWModuleReplaceDetails,
                  willImptIntrOnly: true,
                })
              }
            >
              Yes
            </Button>
            <Button
              sx={{ ...appTheme.commonBtnStyle }}
              onClick={checkForStandardWithRuleSet}
            >
              No
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

SWMForm.propTypes = {
  openSWModuleReplaceDialog: PropTypes.bool,
  setOpenSWModuleReplaceDialog: PropTypes.func,
  handleSWModuleReplace: PropTypes.func,
  selectedSWModuleReplaceDetails: PropTypes.oneOfType([
    PropTypes.shape({
      name: PropTypes.string,
      version: PropTypes.string,
      description: PropTypes.string,
      summary: PropTypes.string,
      supplier_name: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.shape({ map: PropTypes.func }),
      ]),
      keywords: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({ map: PropTypes.func }),
      ]),
      variant_ids: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({ map: PropTypes.func, length: PropTypes.number }),
      ]),
      vf_ids: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({ map: PropTypes.func, length: PropTypes.number }),
      ]),
      reason: PropTypes.string,
      project_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ee_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      swm_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      meta_details: PropTypes.shape({
        linked_meta_link: PropTypes.string,
      }),
    }),
    PropTypes.object,
  ]),
  setSelectedSWModuleReplaceDetails: PropTypes.func,
  vfOptionsList: PropTypes.array,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  selectedZone: PropTypes.any,
  selectedEEComponentDetails: PropTypes.oneOfType([
    PropTypes.shape({
      variant_ids: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({ map: PropTypes.func, length: PropTypes.number }),
      ]),
    }),
    PropTypes.object,
  ]),
  getSWModulesList: PropTypes.func,
  selectedProjectDetails: PropTypes.object,
  selectedSingleVariantDetails: PropTypes.oneOfType([
    PropTypes.shape({
      variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      vehicle_function_details: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.shape({ map: PropTypes.func }),
      ]),
    }),
    PropTypes.object,
  ]),
  variantOptionList: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({ filter: PropTypes.func }),
  ]),
  title: PropTypes.string,
  supplierOptionsList: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({ some: PropTypes.func }),
  ]),
  disableFields: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({ includes: PropTypes.func }),
  ]),
};

export default SWMForm;
